import { Component, effect, ElementRef, input, OnDestroy, output, viewChild } from '@angular/core';
import { uuid } from '@core/helpers';
import { finalize, Observable, Subject, takeUntil, tap } from 'rxjs';
import { PipesModule } from 'src/app/core/pipes/pipes.module';
import { TooltipsModule } from 'src/app/project/features/tooltip/tooltips.module';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { TPointLightweight } from 'src/app/project/modules/points/points.model';
import { EPriority } from 'src/app/project/modules/points/priorities';
import { iconMap } from 'src/app/project/shared/enums/icon-map';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { EStatus } from 'src/app/project/shared/enums/status.enum';
import { DropdownService } from '../../dropdown/dropdown-service/dropdown.service';
import { TDropdown } from '../../dropdown/dropdown.consts';
import { DropdownModule } from '../../dropdown/dropdown.module';
import { MultiStepDropdownContainerComponent } from '../../dropdown/multi-step-dropdown/multi-step-dropdown-container.component';
import { Step } from '../../dropdown/multi-step-dropdown/multi-step-dropdown.component';
import { IconModule } from '../../icon/icon-module';
import { TSelectOption } from '../../input/select/select.model';
import { SelectModule } from '../../input/select/select.module';
import { SpinnerModule } from '../../spinner/spinner.module';
import { RELATED_POINT_TYPE_LABEL_KEY } from '../related-point-type-labels.consts';
import { ERelatedPointType, TRelatedPoint, TSiteLightweight } from '../related-points.consts';
import { RelatedPointsService } from '../related-points.service';

type SelectedPoint = {
  workspaceId: string;
  pointId: string;
  label: string;
  title: string;
  siteName: string;
};

@Component({
  selector: 'pp-new-related-point-row',
  imports: [SelectModule, PipesModule, IconModule, SpinnerModule, DropdownModule, TooltipsModule],
  templateUrl: './new-related-point-row.component.html',
  styleUrl: './new-related-point-row.component.scss',
})
export class NewRelatedPointRowComponent implements OnDestroy {
  ppPointId = input.required<string>();
  ppExcludedPointIds = input<string[]>([]);
  ppAccountId = input.required<string>();
  ppWorkspaceId = input.required<string>();
  ppIsNewPoint = input.required<boolean>();
  ppRelationAdded = output<TRelatedPoint>();
  ppCancelled = output<void>();

  pointButton = viewChild<ElementRef>('pointButton');
  buttonId: string = uuid();
  dropdown: TDropdown = this.dropdownService.getDropdown();
  iconMap = iconMap;
  EIconPath = EIconPath;
  processing = false;
  loadingSites = false;
  displayingPointsDropdown = false;

  steps: Step[] = [];

  relationOptions: TSelectOption[] = Object.entries(RELATED_POINT_TYPE_LABEL_KEY).map(
    ([value, labelKey]) => ({
      label: this.translationPipe.transform(labelKey),
      value: value as ERelatedPointType,
    }),
  );

  selectedRelation: TSelectOption<ERelatedPointType> = this
    .relationOptions[0] as TSelectOption<ERelatedPointType>;

  selectedPoint: SelectedPoint;
  loading = false;

  private sites: TSiteLightweight[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private translationPipe: TranslationPipe,
    private relatedPointsService: RelatedPointsService,
    private dropdownService: DropdownService,
  ) {
    effect(() => {
      this.ppAccountId();
      this.loadSites();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadSites(): void {
    if (!this.ppAccountId()) {
      this.buildSitePointsMenu();

      return;
    }

    this.loadingSites = true;

    this.relatedPointsService
      .fetchSites(this.ppAccountId())
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingSites = false;
        }),
      )
      .subscribe((sites) => {
        this.sites = sites;
        this.buildSitePointsMenu();
      });
  }

  private buildSitePointsMenu(): void {
    this.steps = [
      {
        index: 1,
        group: this.sites.map((site) => ({
          id: site.workspaceId,
          label: site.siteName,
          hideChildrenValues: true,
          labelCallback: (): Observable<TPointLightweight[]> => this.loadPointsForSite(site),
          values: [{ id: '', label: '' }],
        })),
      },
    ];

    this.updateDropdownIfOpen();
  }

  private updateDropdownIfOpen(): void {
    if (!this.displayingPointsDropdown) {
      return;
    }

    setTimeout(() => {
      const dropdown = this.dropdownService.getDropdown();

      if (dropdown.visible) {
        this.dropdownService.setData({
          ...dropdown.data,
          steps: this.steps,
          isLoading: this.loadingSites || this.processing,
        });
      }
    });
  }

  private loadPointsForSite(selectedSite: TSiteLightweight): Observable<TPointLightweight[]> {
    this.processing = true;

    return this.relatedPointsService.fetchPoints(selectedSite.workspaceId).pipe(
      tap((points) => {
        this.displayNextStep(selectedSite, points);
      }),
      takeUntil(this.destroy$),
      finalize(() => {
        this.processing = false;
      }),
    );
  }

  private displayNextStep(
    selectedSite: TSiteLightweight,
    points: { id: string; sequenceNumber: number; title: string }[],
  ): void {
    const stepOne: Step = this.steps.find((step) => step.index === 1);
    const excludedIds = this.ppExcludedPointIds();
    const filteredPoints = points.filter((point) => !excludedIds.includes(point.id));
    const pointValues = filteredPoints.map((point) => ({
      id: point.id,
      label: `${point.sequenceNumber} . ${point.title}`,
    }));

    this.steps = [
      stepOne,
      {
        index: 2,
        emit: (valueId: unknown): void => {
          const point = filteredPoints.find((p) => p.id === valueId);

          if (!point) {
            return;
          }

          this.selectedPoint = {
            workspaceId: selectedSite.workspaceId,
            pointId: point.id,
            label: `${point.sequenceNumber} . ${point.title}`,
            title: point.title,
            siteName: selectedSite.siteName,
          };

          this.dropdownService.hideDropdown();
        },
        group: [
          {
            id: selectedSite.workspaceId,
            label: selectedSite.siteName,
            values: pointValues,
          },
        ],
      },
    ];

    this.dropdownService.setData({ ...this.dropdownService.getDropdown().data, steps: this.steps });
  }

  selectItem(newItem: TSelectOption<ERelatedPointType>): void {
    this.selectedRelation = newItem;
  }

  cancelAdding(): void {
    this.ppCancelled.emit();
  }

  displayPointsDropdown(): void {
    this.displayingPointsDropdown = true;

    const modalData = {
      steps: this.steps,
      selectedId: this.selectedPoint?.pointId ?? null,
      selectedParentId: this.selectedPoint?.workspaceId ?? null,
      isLoading: this.loadingSites || this.processing,
    };

    const buttonWidth = this.pointButton()?.nativeElement?.offsetWidth;

    this.dropdownService.toggleDropdown(
      this.buttonId,
      modalData,
      MultiStepDropdownContainerComponent,
      {
        callback: () => {
          this.displayingPointsDropdown = false;
        },
        width: buttonWidth ? `${buttonWidth}px` : undefined,
      },
    );
  }

  addRelation(): void {
    if (this.processing || !this.selectedPoint) {
      return;
    }

    this.processing = true;

    const relation: TRelatedPoint = {
      type: this.selectedRelation.value as ERelatedPointType,
      pointId: this.selectedPoint.pointId,
      pointTitle: this.selectedPoint.title,
      pointTags: [],
      pointStatus: EStatus.OPEN,
      pointPriority: EPriority.LOW,
      siteId: this.selectedPoint.workspaceId,
      siteName: this.selectedPoint.siteName,
    };

    this.relatedPointsService
      .addRelation({
        sourcePointId: this.ppPointId(),
        relation,
        workspaceId: this.ppWorkspaceId(),
        isNewPoint: this.ppIsNewPoint(),
      })
      .pipe(
        finalize(() => {
          this.processing = false;
        }),
      )
      .subscribe((newRelation) => {
        this.ppRelationAdded.emit(newRelation);
      });
  }
}
